import { JournalColumnDefinition } from '../types/journal';
import { REGISTER_LIST_PREFIX, parseAccountType } from './journalAccountTypes';

/**
 * Between a code and its name in a dropdown item. Only the code is the value, so whatever
 * precedes the first separator is what Bifrost reads from the cell.
 */
export const LIST_ITEM_SEPARATOR = ' – ';

const DIMENSION_LIST_PREFIX = 'dimension:';
/** The lists read from a masterdata endpoint of their own name. */
const ENTITY_LISTS = ['accounts', 'vatCodes', 'currencies', 'accountTypes', 'documentTypes'];

/** One masterdata row as the list endpoints return it; each entity fills its own fields. */
export interface MasterdataListRow {
  code: string | number;
  name?: string;
  description?: string;
  accountType?: 'posting' | 'heading' | 'total';
  /** An account's flag; for a customer, vendor or bank account, which postings it is blocked for. */
  blocked?: boolean | 'none' | 'partial' | 'all';
  directPosting?: boolean;
}

export function columnLetter(index: number): string {
  let remaining = index;
  let letters = '';
  do {
    letters = String.fromCharCode(65 + (remaining % 26)) + letters;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return letters;
}

export function formatListItem(code: string, name?: string | null): string {
  const label = (name ?? '').trim();
  return label === '' || label === code ? code : `${code}${LIST_ITEM_SEPARATOR}${label}`;
}

export function codeOfListItem(value: string): string {
  const at = value.indexOf(LIST_ITEM_SEPARATOR);
  return (at < 0 ? value : value.slice(0, at)).trim();
}

/**
 * The form a code is matched in against its list. A currency is an ISO code, matched without regard to
 * case as the backend checks it, so `usd` reads as USD; an account type by the type it names, as the
 * backend reads it, so `kunde` reads as Customer; every other code must match exactly.
 */
export function matchFormOf(col: JournalColumnDefinition, code: string): string {
  if (col.masterdataEntity === 'currencies') return code.toUpperCase();
  if (col.masterdataEntity === 'accountTypes') return parseAccountType(code) ?? code;
  return code;
}

/**
 * Which list a column draws on: one per entity, one per dimension. Null for a free-text column. An
 * account column's rows may draw on other lists by their type (`accountListOf`).
 */
export function listKeyOf(col: JournalColumnDefinition): string | null {
  switch (col.masterdataEntity) {
    case 'accounts':
    case 'vatCodes':
    case 'currencies':
    case 'accountTypes':
    case 'documentTypes':
      return col.masterdataEntity;
    case 'dimensions':
      return col.dimensionCode ? `${DIMENSION_LIST_PREFIX}${col.dimensionCode}` : null;
    default:
      return null;
  }
}

/** The masterdata path a list is read from, by its key; null for a key no endpoint serves. */
export function masterdataPathOf(key: string): string | null {
  if (key.indexOf(DIMENSION_LIST_PREFIX) === 0) {
    const dimensionCode = key.slice(DIMENSION_LIST_PREFIX.length);
    return dimensionCode ? `/journal/masterdata/dimensions?dimensionCode=${encodeURIComponent(dimensionCode)}` : null;
  }
  if (key.indexOf(REGISTER_LIST_PREFIX) === 0) return `/journal/masterdata/${key.slice(REGISTER_LIST_PREFIX.length)}`;
  return ENTITY_LISTS.indexOf(key) >= 0 ? `/journal/masterdata/${key}` : null;
}

/** Heading and total accounts structure the chart, and a blocked account takes no postings. */
export function isPostableAccount(row: MasterdataListRow): boolean {
  return (row.accountType ?? 'posting') === 'posting' && row.blocked !== true && row.directPosting !== false;
}

/** Whether a list offers a row: an account where it takes postings, a customer, vendor or bank account unless blocked for all. */
function isOffered(key: string, row: MasterdataListRow): boolean {
  if (key === 'accounts') return isPostableAccount(row);
  return key.indexOf(REGISTER_LIST_PREFIX) !== 0 || row.blocked !== 'all';
}

/** The dropdown items for a list, in the ERP's order, each code once. */
export function listItemsOf(key: string, rows: MasterdataListRow[]): string[] {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const row of rows) {
    const code = String(row.code ?? '').trim();
    if (!code || seen.has(code) || !isOffered(key, row)) continue;
    seen.add(code);
    items.push(formatListItem(code, row.name ?? row.description));
  }
  return items;
}
